import {
  CONCIERGE_CHAT_EVENT_COMMAND,
  CONCIERGE_CHAT_EVENT_SET_PROMPT,
  CONCIERGE_SIDEBAR_INSET_PX,
  CONCIERGE_SIDEBAR_WIDTH_PX,
} from './nav/Concierge/constants';
import {
  AIChatCommand,
  AIChatCommandOptions,
  createAIChatCommand,
} from './nav/Concierge/chatCommand';
import { closeAIChat, openAIChat } from './nav/Concierge/aiChatStateStore';
import {
  ActionCallback,
  ActionDefinition,
  ActionRegistryDefinition,
  Suggestion,
} from './nav/Concierge/types';
/** Unified Nav AI rail: card width plus symmetric horizontal padding (matches `AiPanelSlot` column). */
const SHELL_COLUMN_WIDTH_PX =
  CONCIERGE_SIDEBAR_WIDTH_PX + 2 * CONCIERGE_SIDEBAR_INSET_PX;

/**
 * Layout metrics for the chat slide-in panel (`ConciergeCard`).
 * Keep in sync with `src/nav/Concierge/ConciergeCard.tsx` (`CARD_SIZES`, `transition` on the
 * outer `Box`) and the app MUI theme defaults (`transitions.duration.complex`,
 * `transitions.easing.easeOut`, `zIndex.drawer + 1`).
 */
export const CHAT_CARD_LAYOUT = {
  /** Milliseconds; MUI default for `theme.transitions.duration.complex`. */
  transitionDurationMs: 375,
  /** MUI default for `theme.transitions.easing.easeOut`. */
  transitionEasing: 'cubic-bezier(0, 0, 0.2, 1)',
  /**
   * Horizontal footprint from the viewport's right edge when open: panel width + right inset
   * (`sidebarWidth` 450 + `sidebarInset` 10).
   */
  cardWidthPx: CONCIERGE_SIDEBAR_WIDTH_PX + CONCIERGE_SIDEBAR_INSET_PX,
  /**
   * Unified Nav `AiPanelSlot` column when chat is open: symmetric inset on left/right of the
   * card (`sidebarInset` each side) so the border/glow is visible; used for flex width, FAB offset,
   * and legal footer margin.
   */
  shellColumnWidthPx: SHELL_COLUMN_WIDTH_PX,
  /**
   * Space to keep clear on the main region when the Unified Nav AI column is open (e.g. fixed
   * `LegalFooter` `right` offset). Matches `shellColumnWidthPx`: the grid column is flush to the
   * viewport's right edge. The legacy fixed `ConciergeCard` overlay also applied a viewport
   * `right` inset (`sidebarInset`); do not add that here or the main region leaves a visible gap.
   */
  mainReserveRightPxWhenOpen: SHELL_COLUMN_WIDTH_PX,
  /**
   * MUI modal stacking: `theme.zIndex.modal` (1300) + 1 so the panel aligns with modal layer.
   * Hosts without theme access can use this number; prefer `theme.zIndex.modal` when a theme is available.
   */
  zIndex: 1201,
} as const;

/** Metrics-only layout constants (no DOM accessors). */
export type ChatCardLayoutMetrics = typeof CHAT_CARD_LAYOUT;

/**
 * Public chat panel layout API on {@link AISDK.chatCardLayout}, including getters for the
 * launcher and panel root elements (populated when Concierge is mounted).
 */
export interface ChatCardLayout extends ChatCardLayoutMetrics {
  /** Returns the floating AI launcher DOM node, or null if not mounted. */
  getButtonRef: () => HTMLElement | null;
  /** Returns the chat card panel root DOM node, or null if not mounted. */
  getCardRef: () => HTMLElement | null;
}

/**
 * Manifest configuration for defining page context and available tools for AI interactions.
 */
export interface Manifest {
  /** Version of the schema, as it may evolve over time */
  version: number;
  /** Page type identifier (e.g., "invoice_detail", "subscription_detail", etc.) */
  pageType: string;
  /** Business entity information, supplied on CRUD webpages */
  entity?: Entity;
  /** Form fields definition (text fields, checkboxes, date pickers, etc.) */
  fields?: Record<string, FieldDefinition>;
  /** Read-only webpage content sections */
  sections?: Record<string, SectionDefinition>;
  /** Functions that the LLM can call (parameters should be JavaScript primitive types) */
  tools?: Array<ToolDefinition>;
}

/**
 * Represents a business entity on the page.
 */
export interface Entity {
  /** The business object type the page is primarily concerned with */
  type: string;
  /** The primary identifier of the business object */
  id: string;
  /** A user-friendly representation of the object */
  displayName: string;
}

/**
 * Definition for form fields like text inputs, checkboxes, date pickers, etc.
 */
export interface FieldDefinition {
  /** CSS selector to locate the field element */
  selector?: string;
  /** Direct value to bypass DOM scraping */
  value?: string;
  /** Field type (e.g., 'string', 'email', 'currency', etc.) */
  type: string;
  /** Whether this field contains personally identifiable information */
  pii?: boolean;
}

/**
 * Definition for read-only webpage content sections.
 */
export interface SectionDefinition {
  /** CSS selector to locate the section element */
  selector: string;
  /** Maximum number of characters to extract from the section */
  maxChars: number;
}

/**
 * Definition for tools that can be called by the AI.
 */
export interface ToolDefinition {
  /** Unique name identifier for the tool */
  name: string;
  /** Human-readable description of what the tool does */
  description: string;
  /** Parameter definitions with their types (should be JavaScript primitive types) */
  parameters: Record<string, { type: string }>;
}

/**
 * Subscription object returned by register functions for cleanup.
 */
export interface Subscription {
  /** Function to call to unregister and cleanup the subscription */
  unregister: () => void;
}

/**
 * AI SDK interface for managing context and tools for LLM interactions.
 *
 * @example
 * ```tsx
 * function MyComponent({ queryId }) {
 *   const [queryValue, setQueryValue] = useState('');
 *   const [snippet, setSnippet] = useState('');
 *   const isNewQuery = !queryId;
 *
 *   useEffect(() => {
 *     const sdk = window.zAISDK;
 *
 *     // Define page context
 *     sdk.defineContext({
 *       version: 1,
 *       pageType: 'query_create',
 *       entity: {
 *         type: 'query',
 *         id: null,
 *         displayName: 'New Query',
 *       },
 *       fields: {
 *         query: {
 *           selector: '#query-editor',
 *           value: queryValue,
 *           type: 'string',
 *           pii: false,
 *         },
 *       },
 *       sections: {},
 *       tools: [
 *         {
 *           name: 'write_query',
 *           description: 'Write a query and place it in the code editor',
 *           parameters: {
 *             query: { type: 'string' },
 *           },
 *         },
 *       ],
 *     });
 *
 *     // Register a tool and get the unregister function
 *     const toolRegistration = sdk.registerTool('write_query', ({ query }) => {
 *       setQueryValue(query);
 *     });
 *
 *     // Register an action and get the unregister function
 *     const actionRegistration = sdk.registerAction({ name: 'copy_code', astNodeType: 'code' }, (snippet) => {
 *       setSnippet(snippet);
 *     });
 *
 *     // Cleanup: unregister the tool and action when component unmounts or dependencies change
 *     return () => {
 *       toolRegistration?.unregister();
 *       actionRegistration?.unregister();
 *     };
 *   }, [queryValue]);
 *
 *   return <div>My Component</div>;
 * }
 * ```
 *
 * @returns Object containing AI SDK methods:
 * - `defineContext`: Function to define page context using a manifest
 * - `registerTool`: Function to register tools that AI can call
 * - `registerAction`: Function to register actions that AI can call
 * - `resolveContext`: Function to resolve current context state
 * @see {@link https://github.com/zuora-platform/nav-v2/pull/980} - zAISDK implementation reference
 */
export interface AISDK {
  /**
   * Open the AI chat panel and optionally request a new session, prompt fill,
   * and auto-send. This method is safe for pure JS/JSP hosts. The chat input
   * is focused automatically when the active chat is not busy.
   */
  openChat: (options?: AIChatCommandOptions) => void;
  /** Close the AI chat panel. */
  closeChat: () => void;
  /** Define page context using a manifest configuration */
  defineContext: (manifest: Manifest) => Subscription | void;
  /** Clear all suggestions from the registry */
  cleanupSuggestions: () => void;
  /**
   * @deprecated Legacy Concierge API — only used by `src/nav/Concierge/useConciergeChat.ts`.
   * Do not use in new code; the AI Agent architecture manages suggestions differently.
   */
  setSuggestions: (...suggestions: Suggestion[]) => void;
  /** Set the current prompt text in the AI chat */
  setPrompt: (prompt: string) => void;
  /** Register an action that the AI can call */
  registerAction: (
    opts: ActionDefinition,
    fn: ActionCallback,
  ) => Subscription | void;
  /**
   * @deprecated Legacy Concierge API — only used by `src/nav/Concierge/useConciergeChat.ts`.
   * Do not use in new code; the AI Agent architecture manages tool registration differently.
   */
  registerTool: (name: string, fn: Function) => Subscription | void;
  /** Resolve and return the current context state */
  resolveContext: () => any;
  /** Metrics for the chat panel (width, transition, stacking). */
  chatCardLayout: ChatCardLayout;
  /** Chat configuration: optional prefix to add to messages */
  chatPrefix?: string;
  /**
   * Returns a stable frozen snapshot of the tool registry.
   * The reference changes only when a tool is registered or unregistered.
   */
  toolRegistry: () => Readonly<Record<string, Function>>;
  /**
   * Returns a stable frozen snapshot of the action registry.
   * The reference changes only when an action is registered or unregistered.
   */
  actionRegistry: () => Readonly<Record<string, ActionRegistryDefinition>>;
  /**
   * Returns a stable frozen snapshot of the suggestions list.
   * The reference changes only when suggestions are set or cleared.
   */
  suggestions: () => readonly Suggestion[];
  /** Set the floating AI launcher DOM node (called by Concierge on mount/unmount). */
  setChatButtonRef: (el: HTMLElement | null) => void;
  /** Set the chat card panel root DOM node (called by Concierge on mount/unmount). */
  setChatCardRef: (el: HTMLElement | null) => void;
  /** Returns the pending chat command, or undefined if none is queued. */
  pendingChatCommand: () => AIChatCommand | undefined;
  /** Clear the pending chat command after it has been consumed. */
  clearPendingChatCommand: () => void;
  /**
   * Subscribe to action registry changes. Returns an unsubscribe function.
   * The listener is called synchronously after every register or unregister.
   * Designed for use with `useSyncExternalStore`.
   */
  subscribeToActionRegistry: (listener: () => void) => () => void;
}

/**
 * Initializes the global AI SDK instance on the window object.
 *
 * This function sets up the zAISDK singleton with all necessary methods and registries
 * for managing AI interactions, tools, actions, and context. It should only be called
 * once during application initialization.
 *
 * The SDK provides:
 * - Context management through manifest definitions
 * - Tool registration for AI function calling
 * - Action registration for AI-triggered operations
 * - Suggestion management for AI prompts
 * - Context resolution from DOM elements
 *
 * @returns {void} Nothing is returned, but window.zAISDK is populated
 *
 * @example
 * ```typescript
 * // SDK is automatically initialized when this module is imported
 * // Access via window.zAISDK after initialization
 * if (window.zAISDK) {
 *   window.zAISDK.defineContext({ version: 1, pageType: 'dashboard' });
 * }
 * ```
 */
const initAISDK = () => {
  if (typeof window === 'undefined') {
    console.warn('[aisdk] zAISDK is not available');
    return;
  }

  // SDK is a singleton -- only define it once.
  if (window.zAISDK) {
    console.debug('[aisdk] zAISDK is already initialized.');
    return;
  }

  // --- Private state (closure) ---
  const _toolRegistry: Record<string, Function> = {};
  let _toolRegistrySnapshot: Readonly<Record<string, Function>> = Object.freeze(
    {},
  );

  const _actionRegistry: Record<string, ActionRegistryDefinition> = {};
  let _actionRegistrySnapshot: Readonly<
    Record<string, ActionRegistryDefinition>
  > = Object.freeze({});
  const _actionRegistryListeners = new Set<() => void>();

  const _suggestions: Suggestion[] = [];
  let _suggestionsSnapshot: readonly Suggestion[] = Object.freeze([]);

  let _contextManifest: Manifest | undefined;
  let _pendingChatCommand: AIChatCommand | undefined;
  let _chatButtonEl: HTMLElement | null | undefined;
  let _chatCardEl: HTMLElement | null | undefined;

  // Snapshot invalidation — called on every write, not on every read.
  const invalidateToolSnapshot = () => {
    _toolRegistrySnapshot = Object.freeze({ ..._toolRegistry });
  };
  const invalidateActionSnapshot = () => {
    _actionRegistrySnapshot = Object.freeze({ ..._actionRegistry });
    _actionRegistryListeners.forEach((l) => l());
  };
  const invalidateSuggestionsSnapshot = () => {
    _suggestionsSnapshot = Object.freeze([..._suggestions]);
  };

  window.zAISDK = {
    // --- Public snapshot getters ---
    toolRegistry: () => _toolRegistrySnapshot,
    actionRegistry: () => _actionRegistrySnapshot,
    suggestions: () => _suggestionsSnapshot,

    // --- Public DOM ref setters ---
    setChatButtonRef: (el) => {
      _chatButtonEl = el;
    },
    setChatCardRef: (el) => {
      _chatCardEl = el;
    },

    // --- Public pending command management ---
    pendingChatCommand: () => _pendingChatCommand,
    clearPendingChatCommand: () => {
      _pendingChatCommand = undefined;
    },

    subscribeToActionRegistry: (listener) => {
      _actionRegistryListeners.add(listener);
      return () => _actionRegistryListeners.delete(listener);
    },

    // --- Deprecated __* pass-throughs (direct access to mutable state) ---

    /** @deprecated Use {@link toolRegistry} instead. */
    get __toolRegistry() {
      console.warn(
        '[aisdk] __toolRegistry is deprecated. Use toolRegistry() instead.',
      );
      return _toolRegistry;
    },

    /** @deprecated Use {@link actionRegistry} instead. */
    get __actionRegistry() {
      console.warn(
        '[aisdk] __actionRegistry is deprecated. Use actionRegistry() instead.',
      );
      return _actionRegistry;
    },

    /** @deprecated Use {@link suggestions} instead. */
    get __suggestions() {
      console.warn(
        '[aisdk] __suggestions is deprecated. Use suggestions() instead.',
      );
      return _suggestions;
    },

    /** @deprecated Use {@link resolveContext} to read context, or {@link defineContext} to set it. */
    get __contextManifest() {
      console.warn(
        '[aisdk] __contextManifest is deprecated. Use resolveContext() to read or defineContext() to set.',
      );
      return _contextManifest;
    },
    set __contextManifest(val) {
      console.warn(
        '[aisdk] __contextManifest is deprecated. Use defineContext() instead.',
      );
      _contextManifest = val;
    },

    /** @deprecated Use {@link clearPendingChatCommand} to clear. */
    get __pendingChatCommand() {
      console.warn(
        '[aisdk] __pendingChatCommand is deprecated. Use openChat() to set or clearPendingChatCommand() to clear.',
      );
      return _pendingChatCommand;
    },
    set __pendingChatCommand(val) {
      console.warn(
        '[aisdk] __pendingChatCommand is deprecated. Use openChat() to set or clearPendingChatCommand() to clear.',
      );
      _pendingChatCommand = val;
    },

    /** @deprecated Use {@link setChatButtonRef} instead. */
    get __chatButtonEl() {
      console.warn(
        '[aisdk] __chatButtonEl is deprecated. Use chatCardLayout.getButtonRef() to read or setChatButtonRef() to set.',
      );
      return _chatButtonEl;
    },
    set __chatButtonEl(val) {
      console.warn(
        '[aisdk] __chatButtonEl is deprecated. Use setChatButtonRef() instead.',
      );
      _chatButtonEl = val;
    },

    /** @deprecated Use {@link setChatCardRef} instead. */
    get __chatCardEl() {
      console.warn(
        '[aisdk] __chatCardEl is deprecated. Use chatCardLayout.getCardRef() to read or setChatCardRef() to set.',
      );
      return _chatCardEl;
    },
    set __chatCardEl(val) {
      console.warn(
        '[aisdk] __chatCardEl is deprecated. Use setChatCardRef() instead.',
      );
      _chatCardEl = val;
    },

    chatCardLayout: {
      ...CHAT_CARD_LAYOUT,
      getButtonRef: () => _chatButtonEl ?? null,
      getCardRef: () => _chatCardEl ?? null,
    },
    chatPrefix: undefined,
    defineContext: (manifest) => {
      if (!manifest || typeof manifest !== 'object') {
        throw new Error('Context manifest must be an object.');
      }

      _contextManifest = manifest;

      return {
        unregister: function () {
          Object.keys(_actionRegistry).forEach((name) => {
            delete _actionRegistry[name];
            console.debug(`[aisdk] Action "${name}" unregistered.`);
          });
          invalidateActionSnapshot();

          Object.keys(_toolRegistry).forEach((name) => {
            delete _toolRegistry[name];
            console.debug(`[aisdk] Tool "${name}" unregistered.`);
          });
          invalidateToolSnapshot();
        },
      };
    },
    cleanupSuggestions: () => {
      _suggestions.splice(0, Infinity);
      invalidateSuggestionsSnapshot();
      console.debug(
        '[aisdk] All suggestions have been cleared from the registry.',
      );
    },
    openChat: (options) => {
      _pendingChatCommand = createAIChatCommand(options);
      openAIChat();
      window.dispatchEvent(new CustomEvent(CONCIERGE_CHAT_EVENT_COMMAND));
    },
    closeChat: () => {
      closeAIChat();
    },
    setPrompt: (prompt: string) => {
      window.dispatchEvent(
        new CustomEvent(CONCIERGE_CHAT_EVENT_SET_PROMPT, { detail: prompt }),
      );
    },
    setSuggestions: (...suggestions: Suggestion[]) => {
      console.warn(
        '[aisdk] setSuggestions is deprecated. It is only supported for the legacy Concierge API (useConciergeChat). Do not use in new code.',
      );
      if (!Array.isArray(suggestions) || suggestions.length === 0) {
        console.warn('[aisdk] No suggestions provided to setSuggestions.');
        return;
      }

      const filtered = suggestions.filter(
        (suggestion) =>
          suggestion &&
          typeof suggestion.text === 'string' &&
          suggestion.text.length > 0,
      );

      _suggestions.splice(0, Infinity, ...filtered);
      invalidateSuggestionsSnapshot();

      console.debug(
        `[aisdk] ${suggestions.length} suggestion(s) added to the registry.`,
      );

      return {
        unregister: function () {
          _suggestions.splice(0, Infinity);
          invalidateSuggestionsSnapshot();
          console.debug(
            '[aisdk] All suggestions have been cleared from the registry.',
          );
        },
      };
    },
    registerAction: (
      { astNodeType, name, label, filter, context, hideSourceContent },
      func,
    ) => {
      if (typeof name !== 'string' || name.length === 0) {
        throw new Error('Action name must be a non-empty string.');
      }

      if (typeof func !== 'function') {
        throw new Error(`Action "${name}" must be a function.`);
      }

      // Avoid name collisions – do not overwrite existing actions
      if (_actionRegistry[name]) {
        throw new Error(`Action "${name}" is already registered.`);
      }

      _actionRegistry[name] = {
        astNodeType,
        context: context ?? 'menu',
        filter,
        label,
        name,
        hideSourceContent,
        callback: func,
      };
      invalidateActionSnapshot();
      console.debug(`[aisdk] Action "${name}" registered successfully.`);

      return {
        unregister: function () {
          delete _actionRegistry[name];
          invalidateActionSnapshot();
          console.debug(`[aisdk] Action "${name}" unregistered.`);
        },
      };
    },
    registerTool: (name, func) => {
      console.warn(
        '[aisdk] registerTool is deprecated. It is only supported for the legacy Concierge API (useConciergeChat). Do not use in new code.',
      );
      if (typeof name !== 'string' || name.length === 0) {
        throw new Error('Tool name must be a non-empty string.');
      }

      if (typeof func !== 'function') {
        throw new Error(`Tool "${name}" must be a function.`);
      }

      // Avoid name collisions – do not overwrite existing tools
      if (_toolRegistry[name]) {
        throw new Error(`Tool "${name}" is already registered.`);
      }

      _toolRegistry[name] = func;
      invalidateToolSnapshot();
      console.debug(`[aisdk] Tool "${name}" registered successfully.`);

      return {
        unregister: function () {
          delete _toolRegistry[name];
          invalidateToolSnapshot();
          console.debug(`[aisdk] Tool "${name}" unregistered.`);
        },
      };
    },
    resolveContext: () => {
      const manifest = _contextManifest as Manifest;
      if (!manifest) {
        console.warn(
          'No context manifest defined. Call defineContext() first.',
        );
        return {};
      }

      const result = { fields: {}, sections: {} };

      // Resolve each field
      if (manifest.fields) {
        for (const [fieldName, fieldDef] of Object.entries(manifest.fields)) {
          const selector = fieldDef.selector;
          const elem = selector ? document.querySelector(selector) : null;
          let value: any = null;
          if (elem) {
            // If it's an input element, use its value; otherwise, use text content
            if (
              elem instanceof HTMLInputElement ||
              elem instanceof HTMLTextAreaElement
            ) {
              value = elem.value;
            } else {
              value = elem.textContent || '';
            }
            value = value.trim();
            // Optionally, handle special types (e.g., format or validate)
            if (fieldDef.type === 'email') {
              // Basic email normalization example
              value = value.toLowerCase();
            } else if (fieldDef.type === 'currency') {
              // For currency, remove any formatting to get a raw number string (example)
              value = value.replace(/[^0-9.\-]+/g, '');
            }
          }
          result.fields[fieldName] = value;

          // You could decide to exclude PII fields here if needed.
          // We will include them, but mark them in the manifest for awareness.
        }
      }

      // Resolve each section
      if (manifest.sections) {
        for (const [sectionName, sectionDef] of Object.entries(
          manifest.sections,
        )) {
          const selector = sectionDef.selector;
          const elem = document.querySelector(selector);
          let text = '';
          if (elem) {
            // Get the full text content of the section element
            text = elem.textContent || '';
            text = text.trim();
            // Apply maxChars limit if specified
            if (
              typeof sectionDef.maxChars === 'number' &&
              text.length > sectionDef.maxChars
            ) {
              text = text.slice(0, sectionDef.maxChars);
            }
          }
          result.sections[sectionName] = text;
        }
      }

      const merged = Object.assign(result, manifest);
      return merged;
    },
  };

  document.dispatchEvent(
    new CustomEvent('aisdk_initialized', { detail: { aisdk: window.zAISDK } }),
  );

  console.debug('[aisdk] zAISDK initialized successfully.');
};

initAISDK();

/**
 * Global Window interface extension to include the zAISDK.
 *
 * This declaration extends the Window interface to include the zAISDK property,
 * making it accessible globally throughout the application. The SDK includes
 * both public methods and private internal registries for managing state.
 *
 * @interface Window
 * @property {AISDK} zAISDK - The global AI SDK instance
 */
declare global {
  interface Window {
    /** Global AI SDK instance with private registry properties */
    zAISDK: AISDK & {
      /**
       * @deprecated Use {@link AISDK.actionRegistry} instead.
       * Direct mutable reference — mutations bypass snapshot invalidation.
       */
      __actionRegistry: Record<string, ActionRegistryDefinition>;
      /**
       * @deprecated Use {@link AISDK.clearPendingChatCommand} to clear.
       * Latest pending chat command from pure JS hosts.
       */
      __pendingChatCommand?: AIChatCommand;
      /**
       * @deprecated Use {@link AISDK.resolveContext} to read, {@link AISDK.defineContext} to set.
       * Direct mutable reference to the context manifest.
       */
      __contextManifest?: Manifest;
      /**
       * @deprecated Use {@link AISDK.suggestions} instead.
       * Direct mutable reference — mutations bypass snapshot invalidation.
       */
      __suggestions: Suggestion[];
      /**
       * @deprecated Use {@link AISDK.toolRegistry} instead.
       * Direct mutable reference — mutations bypass snapshot invalidation.
       */
      __toolRegistry: Record<string, Function>;
      /**
       * @deprecated Use {@link AISDK.setChatButtonRef} instead.
       * DOM: floating AI launcher root (set by Concierge `FloatingButton`).
       */
      __chatButtonEl?: HTMLElement | null;
      /**
       * @deprecated Use {@link AISDK.setChatCardRef} instead.
       * DOM: chat panel outer surface (set by Concierge `ConciergeCard`).
       */
      __chatCardEl?: HTMLElement | null;
    };
  }
}
